import { HtmlHTMLAttributes, memo, MouseEvent, ReactNode, useCallback, useState } from "react";
import classNames from "classnames";
import { BaseSnackbarTemplateProps } from "../types";
import { SnackbarMessageDetails } from "./SnackbarMessageDetails";

import { ReactComponent as CloseIcon } from "./icons/close.svg";
import { ReactComponent as InfoIcon } from "./icons/info.svg";
import { ReactComponent as OkIcon } from "./icons/ok.svg";

import styles from "./style.module.scss";

export type DefaultSnackbarTemplateProps = BaseSnackbarTemplateProps & {
  title?: string,
  message?: ReactNode,
  type?: "info" | "success" | "error",
  maxTextLength?: number,
} & HtmlHTMLAttributes<HTMLDivElement>

export const DefaultSnackbarTemplate = memo<DefaultSnackbarTemplateProps>((
  {
    className,
    message,
    title,
    type = "info",
    maxTextLength = 100,
    children,
    removeMessage,
    setIsAutoCloseTimeoutDisabled,
    ...restProps
  }) => {
  const [ isOpen, setIsOpen ] = useState(false);

  const showModal = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
    setIsAutoCloseTimeoutDisabled?.(true);
  }, [ setIsOpen, setIsAutoCloseTimeoutDisabled ]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setIsAutoCloseTimeoutDisabled?.(false);
  }, [ setIsOpen, setIsAutoCloseTimeoutDisabled ]);

  return (
    <>
      <div className={classNames(className, styles.root, styles[type as string])} onClick={removeMessage} {...restProps}>
        <i className={styles.icon}>
          {
            (() => {
              switch (type) {
                case "error":
                  return <CloseIcon />;
                case "success":
                  return <OkIcon />;
                case "info":
                  return <InfoIcon />;
              }
            })()
          }
        </i>
        <div className={styles.title}>
          {title}
        </div>
        <div className={styles.body}>
          {
            typeof message === "string"
              ?
              <div>
                {message?.length <= maxTextLength ? message :
                  <div>
                    {`${message?.substring?.(0, maxTextLength)} ...`}
                    <button
                      className={styles.showMore}
                      onClick={showModal}
                    >
                      More details
                    </button>
                  </div>
                }
              </div>
              : message
          }
        </div>
        {children}
      </div>
      {
        typeof message === "string" &&
        <SnackbarMessageDetails message={message} title={title} isOpen={isOpen} onClose={onClose} />
      }
    </>
  );
});

export default DefaultSnackbarTemplate;